import { useEffect, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import Layout from "../components/Layout";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import {
  useGenerateChatBotUrlQuery,
  useGetUserQuery,
} from "features/auth/auth";
import { useAppSelector } from "features/store";

function rtkQueryErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "Something went wrong. Please try again.";
  }
  if ("status" in error && error.status === "FETCH_ERROR") {
    return "Network error. Check your connection and try again.";
  }
  if ("status" in error && error.status === "PARSING_ERROR") {
    return "Could not read the server response.";
  }
  if ("status" in error && error.status === "TIMEOUT_ERROR") {
    return "The request timed out. Try again.";
  }
  if (
    "status" in error &&
    error.status === "CUSTOM_ERROR" &&
    "error" in error
  ) {
    const msg = (error as { error?: unknown }).error;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  const data = (error as FetchBaseQueryError).data;
  if (data != null && typeof data === "object") {
    const d = data as Record<string, unknown>;
    const detail = d.detail ?? d.message ?? d.error;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (
      Array.isArray(detail) &&
      detail.length &&
      typeof detail[0] === "string"
    ) {
      return detail[0];
    }
  }
  return "Unable to load AI Reporting right now. Please try again.";
}

export default function ChatPage() {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const webAccessDenied = useAppSelector((state) => state.auth.webAccessDenied);
  const clientView = useAppSelector((state) => state.clientView);
  const chatBotUrlKey =
    clientView.active && clientView.userId != null
      ? `client-${clientView.userId}`
      : "self";

  const {
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied,
  });

  const {
    data,
    error: chatUrlError,
    isLoading: chatUrlLoading,
    isFetching: chatUrlFetching,
    refetch: refetchChatUrl,
    isSuccess: chatUrlSuccess,
  } = useGenerateChatBotUrlQuery(chatBotUrlKey, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data && typeof data.url === "string" && data.url.trim() !== "") {
      setIframeUrl(data.url.trim());
      return;
    }
    setIframeUrl(null);
  }, [data]);

  const loading = userLoading || chatUrlLoading || chatUrlFetching;
  const chatUrlMissing =
    !loading &&
    !userError &&
    !chatUrlError &&
    chatUrlSuccess &&
    (!data?.url || String(data.url).trim() === "");

  if (loading) {
    return (
      <Layout title="AI Agent" content="AI Agent" noScroll={true}>
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  if (userError) {
    return (
      <Layout title="AI Agent" content="AI Agent">
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            pt: { xs: 3, sm: 4 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            <Typography variant="subtitle2" component="span" fontWeight={700}>
              Unable to verify your session
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {rtkQueryErrorMessage(userError)}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => void refetchUser()}>
            Try again
          </Button>
        </Container>
      </Layout>
    );
  }

  if (chatUrlError || chatUrlMissing) {
    const detail =
      chatUrlError != null ? rtkQueryErrorMessage(chatUrlError) : null;
    return (
      <Layout title="AI Agent" content="AI Agent">
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            pt: { xs: 3, sm: 4 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            <Typography variant="subtitle2" component="span" fontWeight={700}>
              AI Reporting unavailable
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {chatUrlMissing
                ? "The assistant link was not returned. If this persists, contact your Calibrate administrator."
                : detail}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => void refetchChatUrl()}>
            Try again
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="AI Agent" content="AI Agent" noScroll={true}>
      <Container
        component="main"
        maxWidth="xl"
        sx={{
          py: 3,
          width: "100%",
          boxSizing: "border-box",
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {iframeUrl ? (
          <Box
            sx={{
              width: "100%",
              flex: "1 1 0",
              minHeight: 0,
            }}
          >
            <iframe
              title="AI Agent"
              style={{
                width: "100%",
                height: "100%",
                border: 0,
              }}
              src={iframeUrl}
              allow="autoplay; fullscreen"
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Container>
    </Layout>
  );
}
